import Grid from '@mui/material/Grid';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete.tsx';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDealContext } from '../../../utils/context/DealContext.tsx';
import { FiltersDropdown } from '../components/FiltersDropdown.tsx';
import { SectionTitle } from '../components/SectionTitle.tsx';
import { SelectedFilters } from '../components/SelectedFilters.tsx';
import {
  Filters,
  getFilterOptions,
  SupplementalAdQualityField,
  type SupplementalAdQualityFormState,
} from '../constants.ts';

export const ContentAttributesSection = () => {
  const { useContentAttributesFetch } = useDealContext();
  const { setValue, control } = useFormContext<SupplementalAdQualityFormState>();

  const observableContentAttributesFromForm = useWatch({ control, name: 'content_attributes' });
  const contentAttributesOp = observableContentAttributesFromForm?.op ?? 'current';
  const contentAttributesIds = observableContentAttributesFromForm?.ids ?? [];

  const { t } = useTranslation();
  const filterOptions = useMemo(() => getFilterOptions(t), [t]);
  const { isContentAttributesLoading, contentAttributes, refetchContentAttributes } = useContentAttributesFetch();

  useEffect(() => {
    if (contentAttributesOp === Filters.ALLOW) {
      refetchContentAttributes();
    }
  }, [refetchContentAttributes, contentAttributesOp]);

  const handleFilterTypeChange = useCallback(
    (_event, value) => {
      setValue(SupplementalAdQualityField.CONTENT_ATRIBUTES_IDS, []);
      setValue(SupplementalAdQualityField.CONTENT_ATRIBUTES_OP, value);
    },
    [setValue]
  );

  const handleChange = useCallback(
    (value: string[]) => {
      setValue(SupplementalAdQualityField.CONTENT_ATRIBUTES_IDS, value);
    },
    [setValue]
  );

  return (
    <>
      <SectionTitle title={t('Content Attributes')} />
      <Grid container spacing={1}>
        <Grid size={6}>
          <RHFAutoComplete
            name={SupplementalAdQualityField.CONTENT_ATRIBUTES_OP}
            textFieldProps={{ margin: 'dense' }}
            disableClearable={true}
            getOptionLabel={option => filterOptions[option]?.name || option}
            options={Object.keys(filterOptions)}
            onChange={handleFilterTypeChange}
            fullWidth
            data-test="saq-content"
          />
        </Grid>
        {contentAttributesOp === Filters.ALLOW && (
          <Grid size={6}>
            <FiltersDropdown
              options={contentAttributes}
              loading={isContentAttributesLoading}
              selectedOptions={contentAttributesIds}
              placeholder={t('[ Search A Content Attributes ]')}
              onChange={handleChange}
              dataTest="content-search"
            />
          </Grid>
        )}
      </Grid>
      {contentAttributesIds.length > 0 && (
        <SelectedFilters
          selectedOptions={contentAttributesIds}
          handleChange={handleChange}
          options={contentAttributes}
          title={`${t('Content Attributes')} [ ${contentAttributesIds.length} ]`}
          filtersTitle={filterOptions.allow.name}
        />
      )}
    </>
  );
};
